import { useState, useEffect, useRef } from "react";
import { FileText, Upload, Download, Trash2, Loader2, Globe, Lock, Library } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const fmtSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export const LibraryPanel = ({ isAdmin = false }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [shareOnUpload, setShareOnUpload] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(isAdmin ? "/library" : "/library/shared");
      setDocs(data);
    } catch { toast.error("Failed to load library"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("shared", shareOnUpload ? "true" : "false");
      await api.post("/library/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added to library");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await fetch(`${API}/library/${doc.id}/download`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setBusyId(null); }
  };

  const toggleShare = async (doc) => {
    setBusyId(doc.id);
    try {
      const { data } = await api.patch(`/library/${doc.id}/share`);
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, is_shared: data.is_shared } : d)));
      toast.success(data.is_shared ? "Now shared with all clients" : "Set to private");
    } catch { toast.error("Update failed"); }
    finally { setBusyId(null); }
  };

  const remove = async (doc) => {
    setBusyId(doc.id);
    try { await api.delete(`/library/${doc.id}`); toast.success("Document removed"); load(); }
    catch { toast.error("Delete failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      {isAdmin && (
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer select-none" data-testid="library-share-toggle">
            <input type="checkbox" checked={shareOnUpload} onChange={(e) => setShareOnUpload(e.target.checked)} className="accent-zinc-300 w-4 h-4" />
            Publish new uploads to all clients
          </label>
          <label className="btn-metal px-5 py-2.5 rounded-lg text-sm font-medium cursor-pointer flex items-center gap-2" data-testid="library-upload-btn">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploading ? "Uploading…" : "Add document"}
            <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="library-upload-input" disabled={uploading} />
          </label>
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm py-10 text-center">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/12 py-16 text-center text-zinc-500" data-testid="library-empty">
          <Library size={28} strokeWidth={1} className="mx-auto mb-3 opacity-60" />
          {isAdmin ? "No documents in the library yet." : "No shared documents available yet."}
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/8" data-testid="library-list">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-4 px-5 py-4 bg-[#0b0b0b] hover:bg-[#101010] transition-colors" data-testid={`library-row-${d.id}`}>
              <FileText size={20} strokeWidth={1.3} className="text-zinc-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm truncate">{d.original_filename}</div>
                <div className="text-zinc-500 text-xs mt-0.5">{fmtSize(d.size)} · {fmtDate(d.created_at)}{isAdmin && d.uploaded_by ? ` · ${d.uploaded_by}` : ""}</div>
              </div>
              {isAdmin && (
                <button onClick={() => toggleShare(d)} disabled={busyId === d.id}
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${d.is_shared ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" : "border-white/15 text-zinc-400 hover:text-white"}`}
                  data-testid={`library-share-${d.id}`}>
                  {d.is_shared ? <><Globe size={12} /> Shared</> : <><Lock size={12} /> Private</>}
                </button>
              )}
              <button onClick={() => download(d)} disabled={busyId === d.id} className="p-2 text-zinc-400 hover:text-white transition-colors" data-testid={`library-download-${d.id}`}>
                {busyId === d.id ? <Loader2 size={17} className="animate-spin" /> : <Download size={17} />}
              </button>
              {isAdmin && (
                <button onClick={() => remove(d)} disabled={busyId === d.id} className="p-2 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`library-delete-${d.id}`}>
                  <Trash2 size={17} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
